// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PartialKeys<T extends Record<string, any>, R extends keyof T> = Omit<T, R> & Partial<Pick<T, R>>;

export enum ScriptTypes {
  JAVASCRIPT = 'application/javascript',
  PLAIN = 'text/plain',
}

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export interface BaseScript {
  readonly src?: string;
  readonly innerHTML?: string;
  readonly id?: string;
  readonly async?: boolean;
  readonly defer?: boolean;
  readonly type?: string;
  readonly 'data-test-id'?: string;
}

export interface BaseScriptProps extends BaseScript {
  includeIf?: boolean;
}

export interface TrackingScriptProps extends BaseScriptProps {
  readonly ucServiceName: string;
}

export interface TrackingScript extends BaseScriptProps {
  readonly 'data-usercentrics': string;
}

export interface UsercentricsScriptProps extends Omit<BaseScriptProps, 'src' | 'innerHTML'> {
  ucId: string;
}

export interface UsercentricsScript extends BaseScript {
  readonly 'data-settings-id': string;
  readonly mode: string;
}
export interface GTMScriptProps extends Omit<BaseScriptProps, 'src'> {
  analyticsId: string;
}

export interface GtagScriptProps extends Omit<BaseScriptProps, 'src'> {
  analyticsId: string;
  optimizeId: string;
}

export type GTMScript = PartialKeys<TrackingScript, 'data-usercentrics'>;
export type GtagScript = PartialKeys<TrackingScript, 'data-usercentrics'>;

export type TrackingScriptsConfig = {
  /**
   * Control scripts implemented such as GTM, gtag on the website and manage consent settings of the users.
   */
  readonly usercentrics: UsercentricsScriptProps;
  /**
   * Google Tag Manager is a tag management system that acts as common tool for running all different tracking tags
   * such as Google Ads, Google Analytics, Facebook Pixel, Linkedln tags through
   * the single central interface of Google Tag Manager.
   */
  readonly gtm?: GTMScriptProps;
  /**
   * Global site tag (gtag) is a tag management framework that acts as a common tool for running only Google tracking tools
   * such as Google Analytics, Google optimize etc.
   */
  readonly gtag?: GtagScriptProps;
  /**
   * Custom scripts that are not supported by GTM
   */
  readonly scripts?: Record<string, TrackingScriptProps>;
};

export type SupportedSript = TrackingScript | UsercentricsScript | GTMScript | GtagScript;
